import { matchesSearch } from '../assets/js/utils/search.js';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetTrigger, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Menu, X, Sun, Moon, Sparkles, Search, LayoutDashboard, Wallet, Send, Smartphone, Settings, LogOut, ChevronDown } from 'lucide-react';
import { ExistingContent, ExistingDialogs, enhanceControls } from './workspace/legacy-ui';
import { initialMenuMotion, menuMotionReducer } from './workspace/menu-motion';
import './styles.css';
import './responsive.css';
import './select-control.css';
import './finance-bank.css';
import './interface-polish.css';
import './navigation-sessions.css';
import './suite-dashboard.css';
import './mailing-schedule.css';
import './modal-layout.css';
import './deal-transfer.css';
import './appearance.css';
import './accounting.css';

function readUser() {
    try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
}
const themeNames = { light: 'Светлая', adaptive: 'Адаптивная', dark: 'Тёмная' };
function applyTheme(theme) {
    if (!themeNames[theme]) return;
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle('dark', theme !== 'light');
    try { localStorage.setItem('takorna-theme', theme); localStorage.setItem('takorna-theme-version', '2'); } catch {}
    document.dispatchEvent(new CustomEvent('themechange', {detail: theme}));
}
function ThemeButton() {
    const [theme, setTheme] = useState(document.documentElement.dataset.theme || 'dark');
    useEffect(() => {
        const changed = event => setTheme(event.detail);
        const stored = event => { if (event.key === 'takorna-theme' && themeNames[event.newValue]) applyTheme(event.newValue); };
        document.addEventListener('themechange', changed); window.addEventListener('storage', stored);
        return () => { document.removeEventListener('themechange', changed); window.removeEventListener('storage', stored); };
    }, []);
    const next = Object.keys(themeNames)[(Object.keys(themeNames).indexOf(theme) + 1) % 3];
    return <Button className="ui-theme-toggle" variant="outline" size="icon"
        aria-label={`Тема: ${themeNames[theme]}. Включить: ${themeNames[next]}`} title={`Тема: ${themeNames[theme]}; далее: ${themeNames[next]}`}
        onClick={() => applyTheme(next)}>
        <span key={theme} className="theme-icon">{theme === 'light' ? <Sun /> : theme === 'adaptive' ? <Sparkles /> : <Moon />}</span>
    </Button>;

}
function Workspace() {
    const [menuMotion, dispatchMenuMotion] = useReducer(menuMotionReducer, initialMenuMotion);
    const {open:menuOpen, offset:dragY, exitOffset:menuDismissY, entering:menuEntering} = menuMotion;
    const setMenuOpen = open => dispatchMenuMotion({type:open ? 'open' : 'close'});
    const [searchOpen, setSearchOpen] = useState(false);
    const drag = useRef(null);
    function startMenuDrag(event) {
        if (event.button !== 0 || event.target.closest('button')) return;
        dispatchMenuMotion({type:'drag', offset:0});
        drag.current = { x: event.clientX, y: event.clientY, at: performance.now(), id: event.pointerId };
        event.currentTarget.setPointerCapture(event.pointerId);
    }
    function moveMenuDrag(event) {
        const start = drag.current;
        if (!start || start.id !== event.pointerId) return;
        const dx = Math.abs(event.clientX - start.x), dy = event.clientY - start.y;
        if (dx > 18 && dx > Math.abs(dy)) { drag.current = null; dispatchMenuMotion({type:'reset'}); return; }
        dispatchMenuMotion({type:'drag', offset:dy});
    }
    function endMenuDrag(event) {
        const start = drag.current;
        drag.current = null;
        if (start && event.type !== 'pointercancel') {
            const distance = event.clientY - start.y;
            if (distance > 80 || (distance > 32 && distance / Math.max(1, performance.now() - start.at) > .45)) {
                dispatchMenuMotion({type:'dismiss', offset:distance});
                return;
            }
        }
        dispatchMenuMotion({type:'reset'});
    }
    useEffect(() => { if (!menuOpen) drag.current = null; }, [menuOpen]);
    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 769px)');
        const closeOnDesktop = () => { if (desktop.matches) dispatchMenuMotion({type:'close'}); };
        desktop.addEventListener('change', closeOnDesktop);
        closeOnDesktop();
        return () => desktop.removeEventListener('change', closeOnDesktop);
    }, []);
    useEffect(() => {
        if (!menuOpen) return;
        const main = document.querySelector('main');
        const wasInert = main.inert;
        main.inert = true;
        return () => { main.inert = wasInert; };
    }, [menuOpen]);
    const [query, setQuery] = useState('');
    const [user, setUser] = useState(readUser);
    const [module, setModule] = useState('overview');
    const sidebar = document.getElementById('sidebar');
    useEffect(() => { document.body.dataset.workspaceModule = module; }, [module]);
    const allowed = () => true; // Unpaid modules remain visible as an empty preview.
    const navigation = [...sidebar.querySelectorAll('button[data-page]')].filter(button => {
        const access = button.closest('[data-module]')?.dataset.module;
        return access === 'ADMIN' ? user.role === 'admin' : !access || allowed(access);
    });
    const matchingNavigation = navigation.filter(button => matchesSearch(query, [button.textContent]));
    function navigate(page) {
        sidebar.querySelector(`button[data-page="${page}"]`)?.click();
        setSearchOpen(false); setMenuOpen(false); setQuery('');
    }
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const active = sidebar.querySelector('button.active[data-page]');
            const current = active?.closest('[data-module]')?.dataset.module;
            setModule(active?.dataset.page === 'overview' ? 'overview' : current && current !== 'ADMIN' ? current.toLowerCase() : '');
            setUser(readUser());
        });
        observer.observe(sidebar, { attributes: true, childList: true, subtree: true, attributeFilter: ['class', 'style'] });
        const logo = sidebar.querySelector('.logo');
        const home = () => navigate('overview');
        const logoKey = event => { if (['Enter',' '].includes(event.key)) { event.preventDefault(); home(); } };
        if (logo) { logo.setAttribute('role','button'); logo.tabIndex=0; logo.setAttribute('aria-label','TaKorna — панель управления'); logo.addEventListener('click',home); logo.addEventListener('keydown',logoKey); }
        const click = event => { if (event.target.closest('button[data-page], #support-btn, #promo-btn')) setMenuOpen(false); };
        const key = event => { if ((event.metaKey || event.ctrlKey) && event.key === 'k') { event.preventDefault(); setSearchOpen(value => !value); } };
        const userChanged = () => setUser(readUser());
        const workspaceNavigate = () => { setMenuOpen(false); setSearchOpen(false); };
        document.addEventListener('workspaceNavigate', workspaceNavigate);
        document.addEventListener('userAccessChanged', userChanged);
        sidebar.addEventListener('click', click);
        document.addEventListener('keydown', key);
        return () => { logo?.removeEventListener('click',home); logo?.removeEventListener('keydown',logoKey); document.removeEventListener('workspaceNavigate', workspaceNavigate); document.removeEventListener('userAccessChanged', userChanged); observer.disconnect(); sidebar.removeEventListener('click', click); document.removeEventListener('keydown', key); };
    }, [sidebar]);
    return <>
        <a className="ui-skip-link" href="#workspace-content">Перейти к содержимому</a>
        <Sheet open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <header className="ui-topbar" data-react-ui>
            <div className="ui-brand-tools"><button type="button" className="ui-mobile-brand" aria-label="TaKorna — панель управления" onClick={() => navigate('overview')}><img src="/assets/img/logo.svg" alt="" /><span><b>TaKorna</b><small>Business Suite</small></span></button><ThemeButton /></div>
            <Tabs value={module} onValueChange={value => { setModule(value); navigate({overview:'overview', accounting:'home', mailings:'newsletters', sms:'sms'}[value]); }}>
                <TabsList className="module-tabs" aria-label="Разделы сайта">
                    <TabsTrigger value="overview"><LayoutDashboard />Обзор</TabsTrigger>
                    {allowed('accounting') && <TabsTrigger value="accounting"><Wallet />Бухгалтерия</TabsTrigger>}
                    {allowed('mailings') && <TabsTrigger value="mailings"><Send />Рассылки</TabsTrigger>}
                    <TabsTrigger value="sms"><Smartphone />SMS</TabsTrigger>
                </TabsList>
            </Tabs>
            <div className="ui-topbar-actions">
                <Button className="ui-quick-trigger" variant="ghost" size="icon" aria-label="Найти раздел" onClick={() => setSearchOpen(true)}><Search /></Button>
                <DropdownMenu><DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="ui-profile" aria-label={`Профиль: ${user.name || 'Настройки'}`}><Avatar className="size-7"><AvatarFallback className="bg-accent text-primary text-xs">{(user.name || 'T').slice(0,1)}</AvatarFallback></Avatar><span>{user.name || 'Профиль'}</span><ChevronDown className="size-3" /></Button>
                </DropdownMenuTrigger><DropdownMenuContent align="end">
                    <DropdownMenuLabel>{user.login || 'TaKorna'}</DropdownMenuLabel><DropdownMenuSeparator />
                    {window.__TAKORNA_PREVIEW__ && <DropdownMenuItem asChild><a href={user.role === 'admin' ? window.__TAKORNA_USER_PREVIEW_URL__ : window.__TAKORNA_ADMIN_PREVIEW_URL__}>{user.role === 'admin' ? 'Просмотр кабинета пользователя' : 'Просмотр админки (тестовая роль)'}</a></DropdownMenuItem>}
                    <DropdownMenuItem onSelect={() => navigate('settings')}><Settings />Настройки</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => sidebar.querySelector('.exit')?.dispatchEvent(new MouseEvent('click', { bubbles: true }))}><LogOut />Выйти</DropdownMenuItem>
                </DropdownMenuContent></DropdownMenu>
                <SheetTrigger asChild><Button variant="outline" size="icon" className={`ui-burger ${menuOpen ? 'is-open' : ''}`} aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} aria-controls="mobile-navigation-sheet">{menuOpen ? <X /> : <Menu />}</Button></SheetTrigger>
            </div>
        </header>
        <SheetContent side="bottom" showCloseButton={false} id="mobile-navigation-sheet" className="ui-menu-sheet" data-dragging={menuOpen && dragY > 0 || undefined} data-entering={menuEntering || undefined} style={{ transform: `translate(-50%, ${dragY}px)`, '--menu-dismiss-y': `${menuDismissY}px` }} onAnimationEnd={event => { if (event.target === event.currentTarget && event.animationName === 'menu-rise') dispatchMenuMotion({type:'entered'}); }} onOpenAutoFocus={event => { event.preventDefault(); document.querySelector('#mobile-navigation-sheet')?.focus(); }}>
            <SheetTitle className="sr-only">Навигация TaKorna</SheetTitle><SheetDescription className="sr-only">Финансы, Telegram и настройки</SheetDescription>
            <div className="ui-menu-drag" onPointerDown={startMenuDrag} onPointerMove={moveMenuDrag} onPointerUp={endMenuDrag} onPointerCancel={endMenuDrag}>
                <span className="ui-menu-handle" aria-hidden="true" />
            </div>
            <ExistingContent element={sidebar} resetScroll />
        </SheetContent></Sheet>
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}><DialogContent className="ui-quick-dialog" data-react-ui>
            <DialogTitle>Перейти в раздел</DialogTitle><DialogDescription>Все доступные вам инструменты TaKorna</DialogDescription>
            <Input aria-label="Название раздела" placeholder="Название раздела…" value={query} onChange={event => setQuery(event.target.value)} />
            <div className="ui-quick-results">{matchingNavigation.map(button => <Button key={button.dataset.page} variant="ghost" onClick={() => navigate(button.dataset.page)}>{button.textContent.trim()}</Button>)}{!matchingNavigation.length && <p role="status">Разделы не найдены</p>}</div>
        </DialogContent></Dialog>
        <ExistingDialogs />
    </>;
}
function start() {
    if (document.getElementById('takorna-ui')) return;
    const main = document.querySelector('main');
    if (main) { main.id = 'workspace-content'; main.tabIndex = -1; }
    enhanceControls();
    const host = document.createElement('div');
    host.id = 'takorna-ui';
    document.body.append(host);
    const publicThemeSlot = document.getElementById('public-theme-slot');
    createRoot(host).render(<TooltipProvider><>{document.body.classList.contains('workspace') ? <Workspace /> : publicThemeSlot ? createPortal(<ThemeButton />, publicThemeSlot) : <div data-react-ui className="public-theme-control"><ThemeButton /></div>}</></TooltipProvider>);
}
if (document.body.classList.contains('workspace') && !window.componentLoader?.loaded) document.addEventListener('componentsLoaded', start, { once: true });
else start();
